import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Calculator, Search, X, ChevronDown, AlertTriangle, ClipboardList } from 'lucide-react';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import ColumnSettingsModal from '../../components/common/ColumnSettingsModal';
import ActionMenu from '../../components/common/ActionMenu';
import exportEstimateXlsx from './estimateExportXlsx';
import { utilitiesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabase } from '../../context/DatabaseContext';

// ── Assignment model ─────────────────────────────────────────────────────────
// Every Supply_By value must be assigned: IO treatment (physical|comms|omit,
// a true partition) + independent Device / Panels booleans. Every in-use panel
// type must be routed: panels|scada|vfd|omit (+ consolidate flag). Generate is
// BLOCKED until the mapping is complete — this drives dollars.

const IO_CHOICES = [
  { key: 'physical', label: 'Physical' },
  { key: 'comms', label: 'Comms' },
  { key: 'omit', label: 'Omit' },
];
const TAB_CHOICES = [
  { key: 'panels', label: 'Panels' },
  { key: 'scada', label: 'SCADA' },
  { key: 'vfd', label: 'VFD & Starter' },
  { key: 'omit', label: 'Omit' },
];

// First-run suggestions only — user must review and Save before Generate.
const suggestSupplyBy = (value) => {
  if (/deleted|n\/a/i.test(value)) return { io: 'omit', device: false, panels: false };
  if (/vfvi/i.test(value)) return { io: 'comms', device: false, panels: false };
  if (/^(wm|cfci)$/i.test(value)) return { io: 'physical', device: true, panels: true };
  if (/vfci|integral/i.test(value)) return { io: 'physical', device: false, panels: false };
  if (/vendor|si/i.test(value)) return { io: 'comms', device: false, panels: false };
  return { io: null, device: false, panels: false };
};
const suggestPanelType = (type) => {
  if (/vfd/i.test(type)) return { tab: 'vfd', consolidate: false };
  return { tab: 'panels', consolidate: false };
};

const CATEGORY_LABELS = {
  drawing: 'Drawings',
  physicalIo: 'Physical IO',
  commsIo: 'Comms IO',
  device: 'Devices',
  enet: 'ENET',
  serial: 'Serial',
  panels: 'Panels',
  scada: 'SCADA',
  vfd: 'VFD & Starter',
  unitRates: 'Unit Rates',
};

const GRID_COLUMNS = [
  { key: 'pageLabel', label: 'Page Label', width: 220 },
  { key: 'subject', label: 'Subject', width: 130 },
  { key: 'label', label: 'Label', width: 180 },
  { key: 'count', label: 'Count', width: 70, alignment: 'right' },
  { key: 'comments', label: 'Comments', width: 320 },
  { key: 'category', label: 'Category', width: 110 },
];

// Checkbox multi-select styled with the shared filter-dropdown CSS.
const MultiSelectDropdown = ({ label, options, selected, onChange, renderOption }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(new Set());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const openDropdown = () => {
    setPending(new Set(selected.map(String)));
    setSearch('');
    setOpen(true);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => String(renderOption ? renderOption(o) : o).toLowerCase().includes(q));
  }, [options, search, renderOption]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(o => pending.has(String(o.value ?? o)));

  const toggleAll = () => {
    const next = new Set(pending);
    filtered.forEach(o => {
      const v = String(o.value ?? o);
      if (allFilteredSelected) next.delete(v); else next.add(v);
    });
    setPending(next);
  };

  const toggleOne = (o) => {
    const v = String(o.value ?? o);
    const next = new Set(pending);
    if (next.has(v)) next.delete(v); else next.add(v);
    setPending(next);
  };

  const summary = selected.length === 0
    ? '(none)'
    : selected.length === options.length
      ? 'All'
      : selected.length <= 2
        ? selected.join(', ')
        : `${selected.length} selected`;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div className="stui-field-group">
        <label>{label}</label>
        <button
          type="button"
          className="stui-field-input"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, cursor: 'pointer', minWidth: 150, textAlign: 'left' }}
          onClick={() => (open ? setOpen(false) : openDropdown())}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
          <ChevronDown size={14} style={{ flexShrink: 0 }} />
        </button>
      </div>
      {/* .filter-dropdown is position:fixed in App.css (callers position it with
          pixel coords) — override to absolute so it anchors to this wrapper. */}
      {open && (
        <div className="filter-dropdown" style={{ position: 'absolute', top: '100%', left: 0, minWidth: 240, zIndex: 1000 }} onClick={e => e.stopPropagation()}>
          <div className="filter-search">
            <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
          </div>
          <div className="filter-options">
            <label className="filter-option filter-option-select-all">
              <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} />
              <span>(Select All)</span>
            </label>
            {filtered.length === 0 ? (
              <div style={{ padding: '8px 12px', color: 'var(--color-gray-400)', fontStyle: 'italic', fontSize: 'var(--text-sm)' }}>No matches</div>
            ) : (
              filtered.map(o => {
                const v = String(o.value ?? o);
                return (
                  <label key={v} className="filter-option">
                    <input type="checkbox" checked={pending.has(v)} onChange={() => toggleOne(o)} />
                    <span>{renderOption ? renderOption(o) : v}</span>
                  </label>
                );
              })
            )}
          </div>
          <div className="filter-footer">
            <button className="filter-ok-btn" onClick={() => { onChange(Array.from(pending)); setOpen(false); }}>OK</button>
            <button className="filter-cancel-btn" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Assignments modal ────────────────────────────────────────────────────────
const AssignmentsModal = ({ isOpen, onClose, supplyByValues, panelTypes, assignments, onSave }) => {
  const [draft, setDraft] = useState({ supplyBy: {}, panelTypes: {} });

  useEffect(() => {
    if (!isOpen) return;
    // Build draft: saved assignment, else suggestion (io/tab suggestions render
    // as pre-picked but the user still has to Save before Generate unblocks).
    const sb = {};
    supplyByValues.forEach(v => {
      sb[v] = assignments.supplyBy[v] || suggestSupplyBy(v);
    });
    const pt = {};
    panelTypes.forEach(t => {
      pt[t] = assignments.panelTypes[t] || suggestPanelType(t);
    });
    setDraft({ supplyBy: sb, panelTypes: pt });
  }, [isOpen, supplyByValues, panelTypes, assignments]);

  const unassigned = useMemo(() => {
    const sb = supplyByValues.filter(v => !draft.supplyBy[v]?.io);
    const pt = panelTypes.filter(t => !draft.panelTypes[t]?.tab);
    return sb.length + pt.length;
  }, [draft, supplyByValues, panelTypes]);

  if (!isOpen) return null;

  const setSb = (value, patch) => setDraft(d => ({ ...d, supplyBy: { ...d.supplyBy, [value]: { ...d.supplyBy[value], ...patch } } }));
  const setPt = (type, patch) => setDraft(d => ({ ...d, panelTypes: { ...d.panelTypes, [type]: { ...d.panelTypes[type], ...patch } } }));

  // Native radio for now — pending a .stui-radio-input addition to STUI
  // (mirroring .stui-checkbox-input); checkboxes use the existing STUI class.
  const radioCell = (checked, onClick) => (
    <td style={{ textAlign: 'center' }}>
      <input type="radio" checked={checked} onChange={onClick} style={{ cursor: 'pointer', accentColor: 'var(--color-primary)' }} />
    </td>
  );
  const checkCell = (checked, onClick) => (
    <td style={{ textAlign: 'center' }}>
      <input type="checkbox" className="stui-checkbox-input" checked={checked} onChange={onClick} />
    </td>
  );
  const unassignedStyle = { background: 'var(--color-warning-light)' };

  return (
    <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content dev-plan-modal" style={{ width: 780, maxWidth: '95vw', maxHeight: '85vh' }}>
        <div className="modal-header">
          <h3>Estimate Assignments</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">

          <div className="detail-section-header" style={{ marginTop: 0 }}>Supply By</div>
          <div className="stui-label-note" style={{ margin: '0 0 8px' }}>
            IO treatment is exclusive — a point is hardwired (Physical), on a vendor controller (Comms), or not counted.
            Device and Panels are independent: does this supply-by's equipment get purchased / do its panels participate?
          </div>
          <table className="stui-data-grid" style={{ tableLayout: 'auto', width: '100%', marginBottom: 16 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Value</th>
                {IO_CHOICES.map(c => <th key={c.key} style={{ textAlign: 'center' }}>{c.label}</th>)}
                <th style={{ textAlign: 'center' }}>Device</th>
                <th style={{ textAlign: 'center' }}>Panels</th>
              </tr>
            </thead>
            <tbody>
              {supplyByValues.map(v => {
                const a = draft.supplyBy[v] || {};
                return (
                  <tr key={v} style={!a.io ? unassignedStyle : undefined}>
                    <td style={{ fontWeight: 500 }}>{v}{!a.io && <span style={{ color: 'var(--color-warning)', marginLeft: 6, fontSize: 11 }}>(unassigned)</span>}</td>
                    {IO_CHOICES.map(c => radioCell(a.io === c.key, () => setSb(v, { io: c.key })))}
                    {checkCell(!!a.device, () => setSb(v, { device: !a.device }))}
                    {checkCell(!!a.panels, () => setSb(v, { panels: !a.panels }))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="detail-section-header">Panel Types (in use)</div>
          <div className="stui-label-note" style={{ margin: '0 0 8px' }}>
            Each type routes to exactly one tab. Consolidate merges identical panels (same drawing + same BOM) into one "Multiple" column with summed quantities.
          </div>
          <table className="stui-data-grid" style={{ tableLayout: 'auto', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Type</th>
                {TAB_CHOICES.map(c => <th key={c.key} style={{ textAlign: 'center' }}>{c.label}</th>)}
                <th style={{ textAlign: 'center' }}>Consolidate</th>
              </tr>
            </thead>
            <tbody>
              {panelTypes.map(t => {
                const a = draft.panelTypes[t] || {};
                return (
                  <tr key={t} style={!a.tab ? unassignedStyle : undefined}>
                    <td style={{ fontWeight: 500 }}>{t}{!a.tab && <span style={{ color: 'var(--color-warning)', marginLeft: 6, fontSize: 11 }}>(unassigned)</span>}</td>
                    {TAB_CHOICES.map(c => radioCell(a.tab === c.key, () => setPt(t, { tab: c.key })))}
                    {checkCell(!!a.consolidate, () => setPt(t, { consolidate: !a.consolidate }))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <span style={{ marginRight: 'auto', fontSize: 12, color: unassigned > 0 ? 'var(--color-warning)' : 'var(--color-gray-500)' }}>
            {unassigned > 0 ? `${unassigned} unassigned — assign everything to save` : 'All values assigned'}
          </span>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={unassigned > 0} onClick={() => onSave(draft)}>Save Assignments</button>
        </div>
      </div>
    </div>
  );
};

// ── Panel/SCADA matrix (shared) ──────────────────────────────────────────────
const stickyColStyle = { position: 'sticky', left: 0, zIndex: 2, background: 'var(--color-bg-white)' };
const PanelMatrix = ({ columns, itemCodes, emptyText }) => {
  if (!columns || columns.length === 0) {
    return <div style={{ padding: 24, color: 'var(--color-gray-400)', fontStyle: 'italic' }}>{emptyText}</div>;
  }
  return (
    <div style={{ overflow: 'auto', maxHeight: '100%' }}>
      <table className="stui-data-grid" style={{ tableLayout: 'auto', width: 'auto' }}>
        <thead>
          <tr>
            <th style={{ ...stickyColStyle, zIndex: 3, whiteSpace: 'nowrap' }}>Item Code</th>
            {columns.map((p, i) => (
              <th key={`${p.tag}-${i}`} style={{ whiteSpace: 'nowrap' }} title={p.description || ''}>
                <div style={{ fontWeight: 400 }}>{p.drawing}</div>
                <div>{p.tag}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {itemCodes.map(code => (
            <tr key={code}>
              <td style={{ ...stickyColStyle, whiteSpace: 'nowrap', fontWeight: 500 }}>{code}</td>
              {columns.map((p, i) => (
                <td key={`${p.tag}-${i}`} style={{ textAlign: 'right' }}>
                  {p.items?.[code] > 0 ? p.items[code] : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Simple list tables (VFD, UnitRates) ──────────────────────────────────────
const ListTable = ({ headers, rows, emptyText }) => {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: 24, color: 'var(--color-gray-400)', fontStyle: 'italic' }}>{emptyText}</div>;
  }
  return (
    <div style={{ overflow: 'auto', maxHeight: '100%' }}>
      <table className="stui-data-grid" style={{ tableLayout: 'auto', width: '100%' }}>
        <thead>
          <tr>{headers.map(h => <th key={h.key} style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>{h.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {headers.map(h => (
                <td key={h.key} style={{ textAlign: h.align || 'left' }}>{r[h.key] ?? ''}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EstimateExportPage = () => {
  const { notify } = useNotifications();
  const { dbSlug } = useDatabase();
  const storageKey = `dawn.estimateExport.assign.${dbSlug}`;

  // Filter option data
  const [sources, setSources] = useState([]);
  const [supplyByValues, setSupplyByValues] = useState([]);
  const [panelTypes, setPanelTypes] = useState([]);

  // Selections + assignments
  const [selectedSources, setSelectedSources] = useState([]); // empty = all
  const [assignments, setAssignments] = useState({ supplyBy: {}, panelTypes: {}, confirmed: false });
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Results
  const [rows, setRows] = useState([]);
  const [vfdRows, setVfdRows] = useState([]);
  const [panels, setPanels] = useState([]);
  const [panelItemCodes, setPanelItemCodes] = useState([]);
  const [scadaPanels, setScadaPanels] = useState([]);
  const [scadaItemCodes, setScadaItemCodes] = useState([]);
  const [unitRates, setUnitRates] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  // Grid state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState(['category']);
  const [showColSettings, setShowColSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('takeoff');

  // Load dropdown/assignment data, then hydrate saved assignments.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await utilitiesApi.getEstimateExportFilters();
        if (cancelled) return;
        const srcs = res.data.sources || [];
        const sbv = res.data.supplyByValues || [];
        const pts = res.data.panelTypes || [];
        setSources(srcs);
        setSupplyByValues(sbv);
        setPanelTypes(pts);

        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { /* corrupt — ignore */ }
        if (saved) {
          setSelectedSources((saved.sourceIds || []).filter(id => srcs.some(s => s.id === id)).map(String));
          setAssignments({
            supplyBy: saved.supplyBy || {},
            panelTypes: saved.panelTypes || {},
            confirmed: true,
          });
        }
      } catch (err) {
        if (!cancelled) notify.error(err.response?.data?.error || 'Failed to load filter options');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Completeness: confirmed + every live value/type has an assignment. New
  // values appearing after a save show up as unassigned and block Generate.
  const unassignedCount = useMemo(() => {
    const sb = supplyByValues.filter(v => !assignments.supplyBy[v]?.io);
    const pt = panelTypes.filter(t => !assignments.panelTypes[t]?.tab);
    return sb.length + pt.length;
  }, [assignments, supplyByValues, panelTypes]);
  const canGenerate = assignments.confirmed && unassignedCount === 0;

  const handleSaveAssignments = (draft) => {
    const next = { supplyBy: draft.supplyBy, panelTypes: draft.panelTypes, confirmed: true };
    setAssignments(next);
    localStorage.setItem(storageKey, JSON.stringify({
      sourceIds: selectedSources.map(Number),
      supplyBy: draft.supplyBy,
      panelTypes: draft.panelTypes,
    }));
    setShowAssignModal(false);
    notify.success('Assignments saved');
  };

  const generate = useCallback(async () => {
    if (!canGenerate) {
      setShowAssignModal(true);
      return;
    }
    setLoading(true);
    try {
      const body = {
        sourceIds: selectedSources.map(Number),
        supplyBy: supplyByValues.map(v => ({ value: v, ...assignments.supplyBy[v] })),
        panelTypes: panelTypes.map(t => ({ type: t, ...assignments.panelTypes[t] })),
      };
      localStorage.setItem(storageKey, JSON.stringify({
        sourceIds: selectedSources.map(Number),
        supplyBy: assignments.supplyBy,
        panelTypes: assignments.panelTypes,
      }));
      const res = await utilitiesApi.generateEstimateExport(body);
      setRows((res.data.rows || []).map((r, i) => ({ ...r, id: i })));
      setVfdRows(res.data.vfdRows || []);
      setPanels(res.data.panels || []);
      setPanelItemCodes(res.data.panelItemCodes || []);
      setScadaPanels(res.data.scadaPanels || []);
      setScadaItemCodes(res.data.scadaItemCodes || []);
      setUnitRates(res.data.unitRates || []);
      setWarnings(res.data.warnings || []);
      setCategoryCounts(res.data.categoryCounts || null);
      setGenerated(true);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to generate estimate export');
    } finally {
      setLoading(false);
    }
  }, [canGenerate, selectedSources, supplyByValues, panelTypes, assignments, storageKey, notify]);

  const searchFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.pageLabel || '').toLowerCase().includes(q) ||
      (r.label || '').toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  // Search-filtered rows further pared by column filter dropdowns — what the
  // grid shows is what the takeoff sheet gets (other sheets are all-or-nothing).
  const exportRows = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) return searchFiltered;
    return searchFiltered.filter(row =>
      Object.entries(filters).every(([colKey, vals]) => {
        if (!vals || vals.length === 0) return true;
        return vals.includes(String(row[colKey] ?? ''));
      })
    );
  }, [searchFiltered, filters]);

  const handleDownload = async () => {
    if (!generated) {
      notify.warning('Nothing to export — generate first');
      return;
    }
    try {
      await exportEstimateXlsx({
        rows: exportRows,
        panels, panelItemCodes,
        scadaPanels, scadaItemCodes,
        vfdRows, unitRates,
        dbSlug,
      });
      notify.success('Download complete');
    } catch (err) {
      notify.error(`Download failed: ${err.message || err}`);
    }
  };

  const sourceOptions = useMemo(
    () => sources.map(s => ({ value: s.id, display: `${s.projectNumber}${s.projectDesc ? ` — ${s.projectDesc}` : ''}` })),
    [sources]
  );

  // STUI badge-status chips; inline-flex override only so the warning icon aligns.
  const chipFlex = { display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' };

  const tabs = [
    { key: 'takeoff', label: 'IO, Comms & Devices', count: rows.length },
    { key: 'panels', label: 'Panels', count: categoryCounts?.panels ?? 0 },
    { key: 'scada', label: 'SCADA', count: categoryCounts?.scada ?? 0 },
    { key: 'vfd', label: 'VFD & Starter', count: vfdRows.length },
    { key: 'unitrates', label: 'UnitRatesStandard', count: unitRates.length },
  ];

  return (
    <div className="stui-module-container">

      {/* HEADER */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Calculator size={20} />
          <h1>Estimate Export</h1>
          <span className="stui-module-header-meta">
            {loading ? 'Generating…' : generated ? `${searchFiltered.length} of ${rows.length} rows` : canGenerate ? 'Ready — generate when set' : 'Assignments required before generating'}
          </span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-search-wrapper">
            {searchTerm
              ? <X size={16} className="stui-search-icon stui-search-icon-clearable" onClick={() => setSearchTerm('')} />
              : <Search size={16} className="stui-search-icon" />
            }
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search page label / label..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <ActionMenu
            onRefresh={generated ? generate : undefined}
            onModifyView={() => setShowColSettings(true)}
            showModifyView={true}
            onDownload={handleDownload}
          />
        </div>
      </div>

      {/* CONTROLS */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--color-gray-200)' }}>
        <MultiSelectDropdown
          label="Source (empty = all)"
          options={sourceOptions}
          selected={selectedSources}
          onChange={vals => setSelectedSources(vals)}
          renderOption={o => o.display}
        />
        <button className="stui-btn stui-btn-secondary" onClick={() => setShowAssignModal(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ClipboardList size={15} />
          Assignments
          {unassignedCount > 0 && (
            <span className="stui-badge-status stui-badge-status-pending">{unassignedCount}</span>
          )}
        </button>
        <button
          className="stui-btn stui-btn-primary"
          onClick={generate}
          disabled={loading || !canGenerate}
          title={canGenerate ? '' : 'Complete the assignments first — every Supply By value and panel type must be mapped'}
        >
          {loading ? 'Generating…' : 'Generate'}
        </button>
        {!canGenerate && (
          <span style={{ fontSize: 12, color: 'var(--color-warning)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <AlertTriangle size={13} />
            {assignments.confirmed ? `${unassignedCount} new value${unassignedCount !== 1 ? 's' : ''} need assignment` : 'Review and save assignments to enable Generate'}
          </span>
        )}
      </div>

      {/* SUMMARY CHIPS */}
      {generated && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 16px' }}>
          {categoryCounts && Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            categoryCounts[key] != null && (
              <span key={key} className="stui-badge-status stui-badge-status-not-started" style={chipFlex}>
                {label}: {categoryCounts[key].toLocaleString()}
              </span>
            )
          ))}
          {warnings.map(w => (
            <span key={w.code} className="stui-badge-status stui-badge-status-pending" style={chipFlex} title={w.code}>
              <AlertTriangle size={12} /> {w.count.toLocaleString()} — {w.message}
            </span>
          ))}
        </div>
      )}

      {/* TABS — same look as the DynamicModule related-info section */}
      {generated && (
        <div className="stui-related-header" style={{ cursor: 'default' }}>
          <div className="stui-related-tabs">
            {tabs.map(t => (
              <button
                key={t.key}
                className={`stui-related-tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label} <span className="stui-related-tab-count">({t.count.toLocaleString()})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* TAKEOFF GRID */}
      {activeTab === 'takeoff' && (
        <div className="stui-datasheet-view">
          <FilterableDataGrid
            data={searchFiltered}
            allData={rows}
            columns={GRID_COLUMNS}
            filters={filters}
            setFilters={setFilters}
            columnOrder={columnOrder}
            hiddenColumns={hiddenColumns}
            frozenColumns={['pageLabel']}
          />
        </div>
      )}

      {/* PANELS / SCADA MATRICES */}
      {activeTab === 'panels' && (
        <div className="stui-datasheet-view">
          <PanelMatrix columns={panels} itemCodes={panelItemCodes} emptyText="No panels matched the assignments." />
        </div>
      )}
      {activeTab === 'scada' && (
        <div className="stui-datasheet-view">
          <PanelMatrix columns={scadaPanels} itemCodes={scadaItemCodes} emptyText="No panel types are assigned to SCADA." />
        </div>
      )}

      {/* VFD & STARTER */}
      {activeTab === 'vfd' && (
        <div className="stui-datasheet-view">
          <ListTable
            headers={[
              { key: 'pageLabel', label: 'Page Label' },
              { key: 'subject', label: 'Subject' },
              { key: 'label', label: 'Label' },
              { key: 'count', label: 'Count', align: 'right' },
              { key: 'comments', label: 'Comments' },
            ]}
            rows={vfdRows}
            emptyText="No panel types are assigned to VFD & Starter."
          />
        </div>
      )}

      {/* UNIT RATES */}
      {activeTab === 'unitrates' && (
        <div className="stui-datasheet-view">
          <ListTable
            headers={[
              { key: 'indexSheet', label: 'Index Sheet' },
              { key: 'manufacturer', label: 'Manufacturer' },
              { key: 'description', label: 'Description' },
              { key: 'model', label: 'Model Number' },
              { key: 'unitPrice', label: 'Unit Price' },
            ]}
            rows={unitRates}
            emptyText="Nothing generated yet."
          />
        </div>
      )}

      <AssignmentsModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        supplyByValues={supplyByValues}
        panelTypes={panelTypes}
        assignments={assignments}
        onSave={handleSaveAssignments}
      />

      <ColumnSettingsModal
        isOpen={showColSettings}
        onClose={() => setShowColSettings(false)}
        columns={GRID_COLUMNS}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        frozenColumns={['pageLabel']}
        onApply={({ columnOrder: order, hiddenColumns: hidden }) => { setColumnOrder(order); setHiddenColumns(hidden); }}
      />
    </div>
  );
};

export default EstimateExportPage;
